import type { MakeOptional } from '@bp/bp-graphql-types';
import { AddIcon, Button, EmptyState, NoTasksIcon, SearchInput } from '@bp/ui-components';
import { useIsFirstRender } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import type { DashboardEvent } from 'components/Home/getDashboardEvents';
import dayjs from 'dayjs';
import type { mapCalendarEventsToBpEventType } from 'hooks/useCalendarEvents';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import React, { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from 'utils/dimensions';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { EventItem } from '../EventItem/EventItem';
import styles from './EventsList.module.scss';

export type BpEventType = MakeOptional<
  ReturnType<typeof mapCalendarEventsToBpEventType>[0],
  | 'maxAttendees'
  | 'classbookNote'
  | 'classbookNoteContentType'
  | 'classbookHomework'
  | 'classbookHomeworkContentType'
  | 'description'
  | 'duration'
  | 'locale'
  | 'status'
  | 'originDisplayName'
  | 'originUuid'
  | 'originType'
>;

type Events = DashboardEvent[] | BpEventType[];

type EventsListProps<T extends Events> = {
  showClassNames?: boolean;
  isOnWhite?: boolean;
  fullHeight?: boolean;
  showHeader?: boolean;
  showCompactEvents?: boolean;
  hideAddButton?: boolean;
  hideMonthHeadlines?: boolean;
  showActive?: boolean;
  minWidth?: string;
  emptyStateHeight?: string;
  events: T;
  scrollToUuid: string | null;
  randomRedrawString?: string | null;
  onEdit?: (uuid: string) => void;
  onAddClick?: () => void;
  onEventClick?: (bpEvent: T[0], ref?: RefObject<HTMLDivElement>) => void;
  className?: string;
  courseUuid?: string;
};

export function EventsList<T extends Events>({
  showClassNames = false,
  isOnWhite = false,
  fullHeight = false,
  showHeader = false,
  showCompactEvents = false,
  hideAddButton = false,
  hideMonthHeadlines = false,
  showActive = false,
  minWidth,
  emptyStateHeight = '400px',
  events,
  scrollToUuid,
  randomRedrawString,
  onEdit,
  onAddClick,
  onEventClick,
  className,
  courseUuid,
}: EventsListProps<T>) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const perms = usePermissionChecker();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { isPhone } = useWindowDimensions();

  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();
  const isFirstRender = useIsFirstRender();

  const filteredEvents = useMemo(() => {
    return events.filter((event) => event && event.title?.toLowerCase().includes(search.toLowerCase()));
  }, [search, events]);

  const refs: Record<string, RefObject<HTMLDivElement>> = useMemo(() => {
    return filteredEvents.reduce(
      (acc, bpEvent) => {
        acc[bpEvent.uuid] = React.createRef();
        return acc;
      },
      {} as Record<string, RefObject<HTMLDivElement>>,
    );
  }, [filteredEvents]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(randomRedrawString): ignore
  useEffect(() => {
    const currentEventRef = scrollToUuid ? refs[scrollToUuid] : null;
    if (scrollRef && scrollRef.current && currentEventRef && currentEventRef.current) {
      const eventTop = currentEventRef.current.offsetTop - (showCompactEvents ? 15 : 60);
      if (isFirstRender) {
        scrollRef.current.scrollTo({ top: eventTop, behavior: 'instant' });
      } else {
        scrollRef.current.scrollTo({ top: eventTop, behavior: 'smooth' });
      }
    }
  }, [scrollToUuid, randomRedrawString, refs, isFirstRender, showCompactEvents]);

  const classes = classNames(
    styles['events-list'],
    { [styles.white]: isOnWhite, [styles.compact]: showCompactEvents },
    className,
  );

  return (
    <div className={classes} style={{ minWidth: minWidth ?? undefined }}>
      {showHeader && (
        <div className={styles.header}>
          <SearchInput
            name={'search'}
            placeholder={t('common.search')}
            onChange={(event) => setSearch(event.target.value)}
            value={search}
          />
          {perms?.canCreateBpCourseEvents({
            uuid: courseUuid ?? '',
            organization: { uuid: organizationUuid },
          }) &&
            !hideAddButton && (
              <Button
                icon={<AddIcon />}
                hierarchy={!isPhone ? 'secondary' : 'ghost'}
                onClick={() => onAddClick && onAddClick()}
              >
                {!isPhone && t('appointments.addAppointment')}
              </Button>
            )}
        </div>
      )}
      {filteredEvents.length === 0 ? (
        <EmptyState
          title={!showCompactEvents ? t('events.noEvents') : undefined}
          subtitle={showCompactEvents ? t('events.noEvents') : undefined}
          icon={<NoTasksIcon />}
          iconColor='var(--color-primary-light)'
          fitParent
          forcedHeight={emptyStateHeight}
          size={showCompactEvents ? 'small' : 'big'}
        />
      ) : (
        <div
          className={styles['scroll-container']}
          style={{
            height: fullHeight ? `calc(100vh - 400px)` : undefined,
          }}
        >
          {!showCompactEvents && <div className={classNames(styles.shadow, styles.top)}></div>}
          <div ref={scrollRef} className={styles.events}>
            {filteredEvents.map((event, index) => {
              let date: dayjs.Dayjs | null = null;
              if (index > 0) {
                const acc = filteredEvents[index - 1];
                if (acc.start.month() !== event.start.month() || acc.start.year() !== event.start.year()) {
                  date = event.start;
                }
              } else {
                date = event.start;
              }
              return (
                <div className={styles.event} key={event.uuid}>
                  {date && !hideMonthHeadlines && (
                    <div className={styles.date}>{`${dayjs(date).format('MMMM')} ${dayjs(date).format('YYYY')}`}</div>
                  )}
                  <EventItem
                    key={event.uuid}
                    event={event}
                    isActive={showActive && scrollToUuid === event.uuid}
                    isCompact={showCompactEvents}
                    ref={refs[event.uuid]}
                    showClassNames={showClassNames}
                    onClick={
                      onEventClick
                        ? (bpEvent) => {
                            onEventClick(bpEvent);
                          }
                        : undefined
                    }
                    onEdit={event.owner ? onEdit : undefined}
                  />
                </div>
              );
            })}
          </div>
          {!showCompactEvents && <div className={classNames(styles.shadow, styles.bottom)}></div>}
        </div>
      )}
    </div>
  );
}
